import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { TBookSync } from '@/services/conciergeThread';

import { notify, notifyMeta } from '@/lib/notify';
import { CHAT_KEYS, CHAT_MONITOR_KEYS } from '@/constants/queryKeys';
import { CONCIERGE_THREAD_COPY } from '@/constants/messages';
import { conciergeThreadService } from '@/services/conciergeThread';
import { errorUtils } from '@/services/errors';

const syncConciergeBook = async (conciergeID: string) => errorUtils.unwrap(await conciergeThreadService.syncBook(conciergeID));

const syncConciergeBooks = async (conciergeIDs: readonly string[]): Promise<TBookSync> => {
  const books = await Promise.all(conciergeIDs.map(syncConciergeBook));

  return {
    synced: books.reduce((total, book) => total + book.synced, 0),
    failedIDs: books.flatMap(book => book.failedIDs),
  };
};

const useSyncConciergeBooks = () => {
  const queryClient = useQueryClient();

  return useMutation<TBookSync, Error, readonly string[]>({
    meta: notifyMeta('conciergeBookSync'),
    mutationKey: ['chat', 'syncConciergeBooks'],
    mutationFn: syncConciergeBooks,
    onSuccess: async (result) => {
      if (result.failedIDs.length > 0) {
        notify.error(`${result.failedIDs.length} ${CONCIERGE_THREAD_COPY.booksPartlyMoved}`);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CHAT_KEYS.root }),
        queryClient.invalidateQueries({ queryKey: CHAT_MONITOR_KEYS.root }),
      ]);
    },
  });
};

export {
  useSyncConciergeBooks,
};
